// ============================================================
// TMR Portal Admin — Crédito → uma avaliação (rota /credito/aprovacoes/:evaluationId) (26 §9)
// ============================================================
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import { PageHeader, Table, Td } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useDecideManualReview, useManualReview } from '../../data/credit/queries';
import { clinicLabel, isMinor, reasonLabel, registrationSourceLabel } from '../../data/credit/status';
import { formatDay, ruleOptionCopy, SCORE_BANDS_KEY } from '../../data/businessRules/status';
import type { ManualReviewDetail, RegistrationCheckView } from '../../data/credit/contract';
import { BureauReportCard, DebtsTable, Figure, Figures, Muted, Panel, PanelNote, Section, SituationBadge } from './CreditParts';

/**
 * One evaluation with what decided it — the rules and the debts of that moment, the bureau consult it used — and,
 * while it waits, the decision. The note is required and stays with the TMR; the clinic reads a standard sentence.
 */
export function ManualReviewDetailPage() {
  const { evaluationId } = useParams<{ evaluationId: string }>();
  const navigate = useNavigate();
  const canRead = usePermission(PERMISSIONS.creditReviewsRead);
  const query = useManualReview(canRead ? evaluationId : undefined);

  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState message="Você não tem permissão para ver a análise de crédito." />
        </div>
      </div>
    );
  }

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <button
        type="button"
        onClick={() => navigate('/credito/aprovacoes')}
        style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--fg-muted)', fontFamily: 'var(--font-sans)', fontSize: 13.5, padding: 0 }}
      >
        <StrokeIcon d={Icons.chevronL} size={16} /> Aprovação manual
      </button>

      {query.isPending ? (
        <Skeleton rows={6} />
      ) : query.isError ? (
        query.error instanceof ApiError && query.error.isNotFound ? (
          <div className="pa-card">
            <EmptyState message="Avaliação não encontrada." hint="Ela pode ser de uma clínica fora do seu acesso." />
          </div>
        ) : (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        )
      ) : (
        <ReviewContent review={query.data} />
      )}
    </div>
  );
}

function ReviewContent({ review }: { review: ManualReviewDetail }) {
  const navigate = useNavigate();
  const bandsRule = { key: SCORE_BANDS_KEY };

  return (
    <>
      <PageHeader
        eyebrow={clinicLabel(review.clinic)}
        title={review.patientName}
        desc={`CPF ${review.patientDocument} · enviada em ${formatDay(review.evaluatedAt)}`}
        actions={
          <Btn
            variant="secondary"
            icon={Icons.search}
            // The CPF travels in the navigation state, never in the URL (26 §13).
            onClick={() => navigate('/credito/consulta', { state: { document: review.patientDocument } })}
          >
            Consultar cliente
          </Btn>
        }
      />

      <Section title="Avaliação" aside={<SituationBadge situation={review.situation} />}>
        <Panel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="pa-eyebrow">Motivos</span>
            <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink-900)' }}>
              {review.reasons.length === 0 ? '—' : review.reasons.map(reasonLabel).join(' · ')}
            </span>
          </div>
          <Figures>
            <Figure label="Score" value={review.score ?? 'sem score'} />
            <Figure label="Fonte" value={review.source === 'CpfCnpj' ? 'CPF.CNPJ (Serasa fora)' : 'Serasa'} />
            <Figure label="Modo do rating" value={review.mode === 'ativo' ? 'Ativo' : 'Desligado'} />
          </Figures>
          {review.rulesApplied && (
            <PanelNote>
              Regras da clínica nesta avaliação: inadimplente após {review.rulesApplied.delinquencyDays} dias · faixas{' '}
              {review.rulesApplied.scoreBands
                .map((band) => `${band.from}–${band.to} ${ruleOptionCopy(bandsRule, band.action).label}`)
                .join(' · ')}
            </PanelNote>
          )}
        </Panel>
      </Section>

      {review.registration && (
        <RegistrationSection registration={review.registration} source={review.source} reasons={review.reasons} />
      )}

      <Section title="Dívidas na TMR" aside="Como estavam no dia da avaliação">
        {review.debts ? (
          <DebtsTable debts={review.debts} emptyMessage="Nenhuma parcela em aberto na TMR." />
        ) : (
          <Muted>A Vendas não registrou as dívidas desta avaliação.</Muted>
        )}
      </Section>

      <Section title="Serasa">
        {review.bureauReport ? (
          <BureauReportCard report={review.bureauReport} />
        ) : (
          <Muted>
            Esta avaliação não usou uma consulta ao Serasa{review.source === 'CpfCnpj' ? ': o Serasa não respondeu.' : '.'}
          </Muted>
        )}
      </Section>

      <DecisionSection review={review} />
    </>
  );
}

/**
 * The name and birth date the clinic informed, side by side with the bureau's. A difference is why the sale is here
 * with any rating mode, and a minor per the bureau with another date informed is the clinic contesting it — the
 * analyst checks the patient's document before approving.
 */
function RegistrationSection({
  registration,
  source,
  reasons,
}: {
  registration: RegistrationCheckView;
  source: string;
  reasons: string[];
}) {
  const nameDiffers = reasons.includes('nome_divergente');
  const birthDateDiffers = reasons.includes('nascimento_divergente');
  const contested = birthDateDiffers && !!registration.sourceBirthDate && isMinor(registration.sourceBirthDate);
  const day = (date: string | null) => (date ? formatDay(`${date}T12:00:00`) : '—');
  const differs = { color: 'var(--danger)', fontWeight: 600 };

  return (
    <Section title="Cadastro" aside={`Informado pela clínica × ${registrationSourceLabel(source)}`}>
      {contested && (
        <Notice tone="warning">
          Segundo {registrationSourceLabel(source)}, o titular é menor de idade: a clínica contesta a data. Confira o documento
          do paciente antes de aprovar — a TMR não financia menores de 18 anos.
        </Notice>
      )}
      <Table dense columns={[{ label: '', width: 140 }, { label: 'Informado pela clínica' }, { label: registrationSourceLabel(source) }]}>
        <tr>
          <Td dense style={{ color: 'var(--fg-muted)' }}>Nome</Td>
          <Td dense style={nameDiffers ? differs : undefined}>{registration.informedName}</Td>
          <Td dense>{registration.sourceName ?? '—'}</Td>
        </tr>
        <tr>
          <Td dense style={{ color: 'var(--fg-muted)' }}>Nascimento</Td>
          <Td dense style={birthDateDiffers ? differs : undefined}>{day(registration.informedBirthDate)}</Td>
          <Td dense>{day(registration.sourceBirthDate)}</Td>
        </tr>
      </Table>
    </Section>
  );
}

/**
 * The decision: shown once taken; while the evaluation waits, the form — for whoever holds
 * `credit.reviews.manage`. A decision the server refuses (the clinic analysed again or cancelled meanwhile) shows
 * the server's own reason.
 */
function DecisionSection({ review }: { review: ManualReviewDetail }) {
  const canManage = usePermission(PERMISSIONS.creditReviewsManage);
  const decide = useDecideManualReview(review.evaluationId);
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  if (review.decision) {
    const approved = review.decision.outcome === 'approved';
    return (
      <Section title="Decisão da TMR">
        <Panel>
          <div style={{ fontSize: 15, fontWeight: 600, color: approved ? 'var(--forest-700)' : 'var(--danger)' }}>
            {approved ? 'Aprovada' : 'Reprovada'} por {review.decision.decidedByName} em {formatDay(review.decision.decidedAt)}.
            {review.situation === 'pendente' && (
              <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}> A Vendas aplica em até 1 minuto.</span>
            )}
          </div>
          <PanelNote>
            <span style={{ color: 'var(--ink-900)', whiteSpace: 'pre-wrap' }}>{review.decision.note}</span>
          </PanelNote>
        </Panel>
      </Section>
    );
  }

  if (review.situation !== 'pendente') return null;

  if (!canManage) {
    return (
      <Section title="Decisão da TMR">
        <Muted>Aguardando quem pode aprovar ou reprovar vendas.</Muted>
      </Section>
    );
  }

  const submit = (outcome: 'approved' | 'rejected') => {
    setTouched(true);
    if (!note.trim()) return;
    decide.mutate({ outcome, note: note.trim() });
  };

  return (
    <Section title="Decisão da TMR">
      <Panel>
        <div>
          <label className="pa-form-label" htmlFor="review-note">
            Observação <span style={{ color: 'var(--danger)' }}>*</span>
          </label>
          <textarea
            id="review-note"
            className="pa-input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            maxLength={1000}
            disabled={decide.isPending}
            placeholder="Por que aprovar ou reprovar. Fica só com a TMR: a clínica lê um texto padrão."
            style={{
              width: '100%',
              padding: '9px 12px',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              resize: 'vertical',
            }}
          />
          {touched && !note.trim() && (
            <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--danger)' }}>Escreva a observação antes de decidir.</div>
          )}
        </div>

        {decide.isError && (
          <Notice tone="danger">
            {decide.error instanceof ApiError ? decide.error.detail : 'Não foi possível registrar a decisão.'}
          </Notice>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Btn variant="secondary" onClick={() => submit('rejected')} disabled={decide.isPending}>
            Reprovar — a venda é cancelada
          </Btn>
          <Btn variant="primary" icon={Icons.check} onClick={() => submit('approved')} disabled={decide.isPending}>
            {decide.isPending ? 'Registrando…' : 'Aprovar'}
          </Btn>
        </div>
      </Panel>
    </Section>
  );
}
